import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Compass, Gift, Copy, Check } from "@phosphor-icons/react";
import SpecialtyCombobox from "@/components/SpecialtyCombobox";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { SPECIALTIES } from "@/lib/specialties";

const TITLES = ["Mme", "M.", "Dr.", "Pr."];

function splitName(full) {
  const parts = (full || "").trim().split(/\s+/);
  if (parts.length <= 1) return { first_name: parts[0] || "", last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

export default function AccountPanel() {
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();
  const isAdmin = (user?.role || "admin") === "admin";
  const [form, setForm] = useState({ first_name: "", last_name: "", title: "", specialty: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const [referral, setReferral] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/referrals/me").then(({ data }) => { if (alive) setReferral(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const copyCode = () => {
    if (!referral?.code) return;
    navigator.clipboard.writeText(referral.code);
    setRefCopied(true);
    toast.success("Code copié");
    setTimeout(() => setRefCopied(false), 1800);
  };
  const copyLink = () => {
    if (!referral?.link) return;
    navigator.clipboard.writeText(referral.link);
    setLinkCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setLinkCopied(false), 1800);
  };
  const shareLink = () => {
    if (!referral?.link) return;
    const text = `Rejoins-moi sur Questiomed — questionnaires médicaux dématérialisés. Avec mon code ${referral.code}, on gagne chacun 1 mois offert dès ta souscription : ${referral.link}`;
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Message copié"); }
  };

  const [tourBusy, setTourBusy] = useState(false);
  const replayTour = async () => {
    setTourBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { onboarding_completed: false });
      setUser(data);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setTourBusy(false); }
  };

  useEffect(() => {
    if (user) {
      const { first_name, last_name } = splitName(user.name);
      setForm({
        first_name: user.first_name || first_name,
        last_name: user.last_name || last_name,
        title: user.title || "",
        specialty: user.specialty || "",
        phone: user.phone || "",
        address: user.address || "",
      });
    }
  }, [user]);

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Prénom et nom sont requis");
    if (isAdmin && form.specialty && !SPECIALTIES.includes(form.specialty)) {
      return toast.error("Merci de sélectionner une profession dans la liste proposée");
    }
    setBusy(true);
    try {
      const payload = { ...form, name: `${form.first_name.trim()} ${form.last_name.trim()}` };
      // Specialty is a practitioner-only attribute — assistants and research
      // roles don't own the medical identity of the cabinet.
      if (!isAdmin) delete payload.specialty;
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      toast.success("Profil enregistré");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const changePwd = async () => {
    if (!pwd.next || pwd.next.length < 8) return toast.error("Le mot de passe doit contenir au moins 8 caractères");
    if (pwd.next !== pwd.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current || undefined, new_password: pwd.next });
      toast.success("Mot de passe mis à jour");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setPwdBusy(false); }
  };

  if (!user) return null;
  const hasPassword = user.auth_provider !== "google";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
          <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-1">Identité</h2>
          <p className="text-sm text-[#5F758F] mb-5">Ces informations peuvent apparaître dans les emails et documents envoyés à vos patients.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Prénom</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-first-name" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-last-name" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Titre</Label>
              <Select value={form.title || ""} onValueChange={(v) => setForm({ ...form, title: v })}>
                <SelectTrigger className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-title">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  {TITLES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <SpecialtyCombobox value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} />
              </div>
            )}
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="01 23 45 67 89" className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-phone" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email</Label>
              <Input value={user.email} disabled className="mt-1.5 bg-[#F4F7FB] border-[#E1E9F0]" />
              <p className="text-xs text-[#5F758F] mt-1">L'email ne peut pas être modifié.</p>
            </div>
            <div className="sm:col-span-2">
              <AddressAutocomplete value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={busy} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="account-save">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <Compass size={26} weight="duotone" className="text-[#3A8FA5] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-1">Revoir le tutoriel</h2>
              <p className="text-sm text-[#5F758F]">Redécouvrez les fonctionnalités clés en 7 étapes rapides depuis l'accueil.</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={replayTour}
            disabled={tourBusy}
            className="rounded-[10px] shrink-0 font-semibold"
            data-testid="replay-tour-btn"
          >
            {tourBusy ? "…" : "Revoir le tutoriel"}
          </Button>
        </div>

        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
          <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-1">Mot de passe</h2>
          <p className="text-sm text-[#5F758F] mb-5">
            {hasPassword
              ? "Choisissez un mot de passe d'au moins 8 caractères."
              : "Vous vous connectez actuellement avec Google. Vous pouvez définir un mot de passe pour vous connecter aussi par email."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {hasPassword && (
              <div className="sm:col-span-2">
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Mot de passe actuel</Label>
                <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-pwd-current" />
              </div>
            )}
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nouveau mot de passe</Label>
              <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-pwd-next" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Confirmation</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-1.5 bg-white border-[#E1E9F0]" data-testid="account-pwd-confirm" />
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={changePwd} disabled={pwdBusy || !pwd.next} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="account-pwd-save">
              {pwdBusy ? "Mise à jour…" : hasPassword ? "Mettre à jour" : "Définir le mot de passe"}
            </Button>
          </div>
        </div>

        {referral?.eligible && user?.role === "admin" && (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6" data-testid="referral-block">
            <div className="flex items-center gap-2 mb-1">
              <Gift size={20} weight="duotone" className="text-[#3A8FA5]" />
              <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Parrainer un confrère</h2>
            </div>
            <p className="text-sm text-[#5F758F] mb-5">Partagez votre code avec un confrère : vous <strong className="text-[#1E3A5F]">gagnez chacun 1 mois offert</strong> (crédit Stripe automatique sur votre prochaine facture) dès sa souscription à un abonnement payant.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Votre code</Label>
                <div className="mt-1.5 flex gap-2">
                  <div className="flex-1 h-11 rounded-[10px] border-2 border-[#3A8FA5] bg-[#E6F1F5] flex items-center justify-center font-bold tracking-[0.15em] text-[#1E3A5F] text-lg tabular-nums select-all" data-testid="referral-code">
                    {referral.code}
                  </div>
                  <Button variant="outline" onClick={copyCode} className="rounded-[10px] h-11 font-semibold shrink-0" data-testid="referral-copy-code">
                    {refCopied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Lien direct d'inscription</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input readOnly value={referral.link} className="h-11 bg-white border-[#E1E9F0] rounded-[10px] text-[13px]" data-testid="referral-link" />
                  <Button variant="outline" onClick={copyLink} className="rounded-[10px] h-11 font-semibold shrink-0">
                    {linkCopied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div className="rounded-[14px] bg-[#EEF3F8] border border-[#E1E9F0] px-4 py-3 text-center">
                <p className="text-[28px] font-bold text-[#1E3A5F] leading-none tabular-nums" data-testid="referral-count">{referral.count_referred}</p>
                <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mt-1.5">Confrère{referral.count_referred > 1 ? "s" : ""} parrainé{referral.count_referred > 1 ? "s" : ""}</p>
              </div>
              <div className="rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-3 text-center">
                {referral.stripe_credit_cents > 0 ? (
                  <>
                    <p className="text-[28px] font-bold text-[#3A8FA5] leading-none tabular-nums" data-testid="referral-bonus">-{(referral.stripe_credit_cents / 100).toFixed(2).replace(".", ",")} €</p>
                    <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mt-1.5">Crédit sur prochaine facture</p>
                  </>
                ) : (
                  <>
                    <p className="text-[28px] font-bold text-[#3A8FA5] leading-none tabular-nums" data-testid="referral-bonus">0 €</p>
                    <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mt-1.5">Crédit sur prochaine facture</p>
                  </>
                )}
              </div>
            </div>

            <p className="text-[12px] text-[#5F758F] mb-4 leading-relaxed">
              💡 Dès que votre filleul souscrit à un abonnement payant, un <strong className="text-[#1E3A5F]">crédit d'1 mois</strong> vous est appliqué automatiquement sur votre prochaine facture. Votre filleul reçoit également <strong className="text-[#1E3A5F]">1 mois offert</strong> sur sa première facture.
            </p>

            <Button onClick={shareLink} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold w-full sm:w-auto" data-testid="referral-share">
              <Gift size={16} className="mr-2" /> Partager le lien
            </Button>
          </div>
        )}
      </div>

      <aside className="lg:col-span-1">
        <div className="bg-[#F4F7FB] border border-[#E1E9F0] rounded-2xl p-6 sticky top-24">
          <Lock size={26} weight="duotone" className="text-[#3A8FA5] mb-3" />
          <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F] mb-2">Sécurité du compte</h3>
          <p className="text-sm text-[#5F758F]">
            Toute modification de votre profil est journalisée dans le journal d'audit RGPD, y compris les changements de mot de passe.
          </p>
        </div>
      </aside>
    </div>
  );
}
